import type { AppNotification, NotificationDeepLink } from './types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Format an ISO timestamp as "Jan 15, 2:30 PM" (no timezone). All `new Date()`
 * usage is contained here so component render bodies stay pure (React 19 lint).
 */
export function formatNotificationTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const month = MONTHS[d.getMonth()];
  const day = d.getDate();

  let hours = d.getHours();
  const minutes = d.getMinutes();
  const meridiem = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  if (hours === 0) hours = 12;
  const mm = minutes.toString().padStart(2, '0');

  return `${month} ${day}, ${hours}:${mm} ${meridiem}`;
}

/** A target id we cannot resolve to a live record (mirrors the seed's `tkt-removed`). */
function isResolvableTarget(targetId: string): boolean {
  const id = targetId.trim().toLowerCase();
  if (!id) return false;
  return !id.includes('removed') && !id.includes('unknown') && !id.includes('deleted');
}

export type DeepLinkResolution = { href: string } | { error: string };

const UNAVAILABLE_COPY: Record<NotificationDeepLink['kind'], string> = {
  device: 'This device is no longer available.',
  subscription: 'This subscription is no longer available.',
  warranty_claim: 'This warranty claim is no longer available.',
  support_ticket: 'This support ticket is no longer available.',
};

/**
 * Resolve a notification to a navigable href, or an error string when its target
 * is missing / unresolvable. Routing table (story 101848):
 *   device         → /devices/:id
 *   subscription   → /subscriptions
 *   warranty_claim → /warranty/:id (placeholder)
 *   support_ticket → /support/:id
 * Pure: no router/query access, so it is safe to call in a render body.
 */
export function resolveDeepLink(n: AppNotification): DeepLinkResolution {
  const link = n.deepLink;
  if (!link) return { error: 'This notification has no destination.' };

  const unavailable = { error: UNAVAILABLE_COPY[link.kind] };

  switch (link.kind) {
    case 'device':
      return isResolvableTarget(link.targetId)
        ? { href: `/devices/${link.targetId}` }
        : unavailable;
    case 'subscription':
      // Subscriptions land on the list (no per-id route this slice).
      return isResolvableTarget(link.targetId) ? { href: '/subscriptions' } : unavailable;
    case 'warranty_claim':
      return isResolvableTarget(link.targetId)
        ? { href: `/warranty/${link.targetId}` }
        : unavailable;
    case 'support_ticket':
      return isResolvableTarget(link.targetId)
        ? { href: `/support/${link.targetId}` }
        : unavailable;
    default:
      return unavailable;
  }
}
